import { PageRef } from '@wix/document-services-types';
import {
  BaseError,
  createErrorBuilder,
} from '@wix/public-editor-platform-errors';
import {
  ComponentType,
  StaticContainer,
} from '@wix/ambassador-devcenter-components-v1-component/types';

export enum UnifiedComponentsInstallationErrorCode {
  provisionAppsFailed = 'provisionAppsFailed',
  noAppRecivedFromProvision = 'noAppRecivedFromProvision',
  nonUnifiedInstallationFailed = 'nonUnifiedInstallationFailed',
  nonUnifiedComponentInUnifiedApp = 'nonUnifiedComponentInUnifiedApp',
  addingUnifiedComponentFailed = 'addingUnifiedComponentFailed',
  homePageNavigationFailed = 'homePageNavigationFailed',
  addingCodePackagesFailed = 'addingCodePackagesFailed',
  runningEditorScriptFailed = 'runningEditorScriptFailed',
  navigateToAppEndPageFailed = 'navigateToAppEndPageFailed',
  failedToLoadHandleManifests = 'failedToLoadHandleManifests',
}

type ComponentTypeMapping = {
  [ComponentType.UNIFIED_LIGHTBOX]: UnifiedComponents;
  [ComponentType.UNIFIED_PAGE]: UnifiedComponents;
};

export const errorTypeMapping: ComponentTypeMapping = {
  [ComponentType.UNIFIED_LIGHTBOX]: 'unified-lightbox',
  [ComponentType.UNIFIED_PAGE]: 'unified-page',
};

type UnifiedComponents =
  | 'app-router'
  | 'builder-component'
  | 'non-unified-component'
  | 'unified-lightbox'
  | 'unified-page'
  | 'unified-widget';

export type UnifiedComponentsError =
  | UnifiedComponentsInstallationError
  | AddUnifiedComponentsError;

const inferExtraDetailsForBI = (
  error: UnifiedComponentsError,
): {
  errorCode: string;
  errorMessage: string;
  errorData: string;
  errorDetails: string;
  errorType: string;
  type?: UnifiedComponents;
  appDefinitionId?: string;
} => {
  return {
    errorCode: error.code,
    errorMessage: error?.getMessage(),
    errorData: error?.getDisplayMessage(),
    errorDetails: error?.getBreadcrumbs()?.toString(),
    errorType: '',
    type: error.componentType,
    appDefinitionId: error.appDefIds,
  };
};

export class UnifiedComponentsInstallationError extends BaseError<UnifiedComponentsInstallationErrorCode> {
  constructor(message: string, code: UnifiedComponentsInstallationErrorCode) {
    super(message, code, 'Unified Components Installation Error');
  }
  componentType?: UnifiedComponents;

  withAppDefIds(appDefinitionId: string | string[]) {
    this.state.appDefinitionId = Array.isArray(appDefinitionId)
      ? JSON.stringify(appDefinitionId)
      : appDefinitionId;
    return this;
  }

  withComponentGUID(componentGUID: string, type: UnifiedComponents) {
    this.state.componentGUID = componentGUID;
    this.state.componentType = type;
    return this;
  }

  withStaticContainer(staticContainer: StaticContainer | undefined) {
    this.state.staticContainer = staticContainer || '';
    return this;
  }

  getExtraDetailsForBI() {
    return inferExtraDetailsForBI(this);
  }

  get appDefIds() {
    return this.state.appDefinitionId;
  }
}

export const createUnifiedComponentsInstallationError = createErrorBuilder<
  UnifiedComponentsInstallationErrorCode,
  UnifiedComponentsInstallationError
>(UnifiedComponentsInstallationError);

export enum AddUnifiedComponentsErrorCode {
  addingBlocksWidgetFailed = 'addingBlocksWidgetFailed',
  addingBuilderComponentFailed = 'addingBuilderComponentFailed',
  addingCustomElementWidgetFailed = 'addingCustomElementWidgetFailed',
  addingRouterFailed = 'addingRouterFailed',
  addingWidgetIntoSectionFailed = 'addingWidgetIntoSectionFailed',
  applyingPresetsFailed = 'applyingPresetsFailed',
  creatingNewPageFailed = 'creatingNewPageFailed',
  creatingNewSectionFailed = 'creatingNewSectionFailed',
  gettingLightboxContainer = 'gettingLightboxContainer',
  manifestLoadingFailed = 'manifestLoadingFailed',
  connectPageToRouterFailed = 'connectPageToRouterFailed',
  pageHaveNoSlugDefinition = 'pageHaveNoSlugDefinition',
  setWidgetFullWidthFailed = 'setWidgetFullWidthFailed',
  widgetGuidNotFound = 'widgetGuidNotFound',
  widgetTypeNotSupported = 'widgetTypeNotSupported',
}

class AddUnifiedComponentsError extends BaseError<AddUnifiedComponentsErrorCode> {
  constructor(message: string, code: AddUnifiedComponentsErrorCode) {
    super(message, code, 'Add Unified Component Error');
  }
  pageRef?: PageRef;
  isUnifiedPage?: boolean;
  componentType?: UnifiedComponents;
  builderComponentType?: string;

  withUserComponentId(componentId: string, type: UnifiedComponents) {
    this.componentType = type;
    this.state.componentId = componentId;
    return this;
  }

  withBuilderComponentType(type: string) {
    this.builderComponentType = type;
    return this;
  }

  withPageRef(pageRef: PageRef, isUnified: boolean) {
    this.pageRef = pageRef;
    this.isUnifiedPage = isUnified;
    this.state.pageRef = JSON.stringify(pageRef);
    return this;
  }

  getExtraDetailsForBI() {
    return {
      ...inferExtraDetailsForBI(this),
      pageType: this.isUnifiedPage ? 'unified-page' : 'editor',
      pageId: this.pageRef?.pageId,
    };
  }

  withAppDefIds(appDefinitionId: string | string[]) {
    this.state.appDefinitionId = Array.isArray(appDefinitionId)
      ? JSON.stringify(appDefinitionId)
      : appDefinitionId;
    return this;
  }

  get appDefIds(): string | undefined {
    return this.state.appDefinitionId;
  }
}

export const createAddUnifiedComponentsError = createErrorBuilder<
  AddUnifiedComponentsErrorCode,
  AddUnifiedComponentsError
>(AddUnifiedComponentsError);
